import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForwardMessageState } from "@/zustand/ForwardMessageState";
import { MessageEntity, ChatEntity } from "@/indexedDB/types";
import { forwardMessageToChat } from "@/middleware/messages";
import { toast } from "@/hooks/useToast";

export function useForwardMessage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const closeForwardModal = useForwardMessageState((s) => s.closeForwardModal);
  const setIsForwarding = useForwardMessageState((s) => s.setIsForwarding);

  const forwardMessage = async (
    message: MessageEntity,
    targetChat: ChatEntity,
  ) => {
    // Edge case: prevent forwarding to the same chat the message came from
    if (message.chatId === targetChat.id) {
      toast({
        duration: 1500,
        description: t("cannotForwardToSameChat"),
        variant: "destructive",
      });
      return;
    }

    closeForwardModal();
    setIsForwarding(true);

    // Navigate to the target chat — useChatRouteSync will update Zustand state.
    navigate(`/${targetChat.id}`);

    try {
      await forwardMessageToChat({
        chatId: targetChat.id,
        messageToForward: message,
      });
    } catch (error) {
      toast({
        duration: 2000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setIsForwarding(false);
    }
  };

  return { forwardMessage };
}
